export type SoapSection = "subjective" | "objective" | "assessment" | "plan";
export type OrderLane = "one-day" | "continuous";
export type OrderCategory = "medication" | "lab" | "procedure" | "fluid" | "other";

export type ParsedOrder = {
  id: string;
  category: OrderCategory;
  label: string;
  details: string;
  lane: OrderLane;
  stat: boolean;
  source?: "parsed" | "heartbeat" | "template" | "manual" | "dictation" | "agent" | "catalog";
  conflict?: string;
  orderedBy?: string;
  orderedAt?: string;
  /** True when the order was auto-drafted by AI (auto-pilot) — drives AI styling. */
  aiGenerated?: boolean;
  /** TMT (Thai Medicines Terminology) concept id for a coded drug order. */
  tmtId?: string;
  /** Default dispense quantity (from the drug catalog). */
  quantity?: number;
};

export type AiAuditContext = {
  action: string;
  userInput: string;
  aiSuggestion: string;
  selectedOutput: string;
  evidence: string;
  model: string;
};

export type GhostSuggestion = {
  field: SoapSection;
  text: string;
  evidence: string;
  why: string;
  model?: string;
  mode?: "live" | "simulation";
};

export type TemplatePreview = {
  id: string;
  name: string;
  subtitle: string;
  soap: Partial<Record<SoapSection, string>>;
  orders: ParsedOrder[];
};

/** A single searchable formulary / order-catalog entry. */
export type CatalogItem = {
  name: string;
  category: OrderCategory;
  defaultLane: OrderLane;
  defaultDetails: string;
  keywords: string[];
};

const delay = (ms = 300 + Math.round(Math.random() * 500)) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const makeId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const orderMeta = () => ({
  orderedBy: "Dr. Heartbeat",
  orderedAt: new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date()),
});

// ---------------------------------------------------------------------------
// Order catalog (searchable formulary) — powers the omnibox autocomplete and
// improves parser accuracy.
// ---------------------------------------------------------------------------

export const orderCatalog: CatalogItem[] = [
  // Medications
  { name: "Paracetamol 500 mg", category: "medication", defaultLane: "one-day", defaultDetails: "1 tab PO q6h PRN fever/pain", keywords: ["paracetamol", "pct", "para", "acetaminophen", "pcm"] },
  { name: "Ibuprofen 400 mg", category: "medication", defaultLane: "continuous", defaultDetails: "1 tab PO tid pc", keywords: ["ibuprofen", "brufen", "nsaid"] },
  { name: "Amoxicillin 500 mg", category: "medication", defaultLane: "continuous", defaultDetails: "1 cap PO tid × 5 days", keywords: ["amoxicillin", "amox", "augmentin"] },
  { name: "Ceftriaxone 2 g", category: "medication", defaultLane: "continuous", defaultDetails: "IV OD", keywords: ["ceftriaxone", "cef", "rocephin"] },
  { name: "Omeprazole 20 mg", category: "medication", defaultLane: "continuous", defaultDetails: "1 cap PO OD ac", keywords: ["omeprazole", "omep", "ppi", "losec"] },
  { name: "Metformin 500 mg", category: "medication", defaultLane: "continuous", defaultDetails: "1 tab PO bid pc", keywords: ["metformin", "glucophage"] },
  { name: "Insulin RI (sliding scale)", category: "medication", defaultLane: "continuous", defaultDetails: "SC AC & HS per scale", keywords: ["insulin", "ri", "actrapid", "sliding scale"] },
  { name: "Furosemide 40 mg", category: "medication", defaultLane: "one-day", defaultDetails: "IV STAT", keywords: ["furosemide", "lasix", "frusemide"] },
  { name: "Enoxaparin 40 mg", category: "medication", defaultLane: "continuous", defaultDetails: "SC OD", keywords: ["enoxaparin", "clexane", "lmwh"] },
  { name: "Warfarin 2 mg", category: "medication", defaultLane: "continuous", defaultDetails: "PO HS", keywords: ["warfarin", "orfarin", "coumadin"] },
  { name: "Tramadol 50 mg", category: "medication", defaultLane: "one-day", defaultDetails: "IV q6h PRN pain", keywords: ["tramadol", "ultram"] },
  { name: "Ondansetron 4 mg", category: "medication", defaultLane: "one-day", defaultDetails: "IV PRN nausea", keywords: ["ondansetron", "zofran", "antiemetic"] },
  // Fluids
  { name: "NSS 1,000 mL", category: "fluid", defaultLane: "continuous", defaultDetails: "IV 80 mL/hr", keywords: ["nss", "normal saline", "0.9", "saline"] },
  { name: "5% Dextrose (D5W) 1,000 mL", category: "fluid", defaultLane: "continuous", defaultDetails: "IV 80 mL/hr", keywords: ["d5w", "dextrose", "d5", "5%d"] },
  { name: "Ringer's lactate 1,000 mL", category: "fluid", defaultLane: "continuous", defaultDetails: "IV 100 mL/hr", keywords: ["ringer", "rls", "lactated", "lr"] },
  { name: "KCl in NSS", category: "fluid", defaultLane: "continuous", defaultDetails: "IV per potassium protocol", keywords: ["kcl", "potassium", "k+"] },
  // Labs
  { name: "CBC", category: "lab", defaultLane: "one-day", defaultDetails: "Next morning 08:00", keywords: ["cbc", "complete blood count", "hb", "hct", "hemoglobin"] },
  { name: "Electrolytes (Chem7)", category: "lab", defaultLane: "one-day", defaultDetails: "Next morning 08:00", keywords: ["chem7", "electrolyte", "electrolytes", "na", "k", "bun", "cr", "creatinine"] },
  { name: "PT/INR", category: "lab", defaultLane: "one-day", defaultDetails: "Next morning 08:00", keywords: ["inr", "pt", "coagulation", "coag"] },
  { name: "Troponin", category: "lab", defaultLane: "one-day", defaultDetails: "Now, repeat in 3 hr", keywords: ["troponin", "trop", "cardiac enzyme"] },
  { name: "Serum lactate", category: "lab", defaultLane: "one-day", defaultDetails: "Now, repeat in 2 hr", keywords: ["lactate", "lactic"] },
  { name: "Blood culture ×2", category: "lab", defaultLane: "one-day", defaultDetails: "Before antibiotics", keywords: ["blood culture", "hemoculture", "h/c", "culture"] },
  { name: "Urinalysis", category: "lab", defaultLane: "one-day", defaultDetails: "Clean-catch specimen", keywords: ["urinalysis", "ua", "urine"] },
  { name: "Stool occult blood", category: "lab", defaultLane: "one-day", defaultDetails: "Next specimen", keywords: ["stool occult", "occult blood", "fobt", "stool"] },
  { name: "Arterial blood gas", category: "lab", defaultLane: "one-day", defaultDetails: "On room air", keywords: ["abg", "blood gas", "arterial"] },
  // Procedures
  { name: "12-lead ECG", category: "procedure", defaultLane: "one-day", defaultDetails: "Bedside now", keywords: ["ecg", "ekg", "electrocardiogram", "12-lead"] },
  { name: "Chest X-ray (AP)", category: "procedure", defaultLane: "one-day", defaultDetails: "Portable", keywords: ["cxr", "chest x-ray", "chest xray", "x-ray", "xray"] },
  { name: "CT scan", category: "procedure", defaultLane: "one-day", defaultDetails: "Per protocol", keywords: ["ct", "ct scan", "computed tomography"] },
  { name: "Ultrasound abdomen", category: "procedure", defaultLane: "one-day", defaultDetails: "Fasting if feasible", keywords: ["ultrasound", "us", "usg", "sonography"] },
  { name: "Echocardiogram", category: "procedure", defaultLane: "one-day", defaultDetails: "Transthoracic", keywords: ["echo", "echocardiogram", "tte"] },
  { name: "Foley catheter", category: "procedure", defaultLane: "continuous", defaultDetails: "Monitor urine output", keywords: ["foley", "catheter", "urinary catheter"] },
  { name: "Wound dressing", category: "procedure", defaultLane: "continuous", defaultDetails: "Daily", keywords: ["dressing", "wound care", "wound"] },
  // Other / supportive
  { name: "Oxygen therapy", category: "other", defaultLane: "continuous", defaultDetails: "Keep SpO₂ ≥ 94%", keywords: ["oxygen", "o2", "spo2", "nasal cannula"] },
  { name: "NPO", category: "other", defaultLane: "continuous", defaultDetails: "Nil per os", keywords: ["npo", "nil by mouth", "fasting"] },
  { name: "PRC transfusion 1 unit", category: "other", defaultLane: "one-day", defaultDetails: "Confirm consent & threshold", keywords: ["prc", "transfusion", "packed red", "blood"] },
  { name: "Physiotherapy", category: "other", defaultLane: "continuous", defaultDetails: "Chest PT / mobilization", keywords: ["physiotherapy", "physio", "pt referral", "mobilization"] },

  // Extended labs
  { name: "Liver function test (LFT)", category: "lab", defaultLane: "one-day", defaultDetails: "Next morning 08:00", keywords: ["lft", "liver function", "ast", "alt", "sgot", "sgpt", "bilirubin", "albumin"] },
  { name: "Renal function test (BUN/Cr)", category: "lab", defaultLane: "one-day", defaultDetails: "Next morning 08:00", keywords: ["rft", "renal", "bun", "creatinine", "cr", "egfr", "kidney"] },
  { name: "HbA1c", category: "lab", defaultLane: "one-day", defaultDetails: "Fasting not required", keywords: ["hba1c", "a1c", "glycated", "glycohemoglobin", "diabetes"] },
  { name: "Fasting blood sugar (FBS)", category: "lab", defaultLane: "one-day", defaultDetails: "Fasting 8 hr", keywords: ["fbs", "fasting glucose", "fasting blood sugar", "dtx"] },
  { name: "Lipid profile", category: "lab", defaultLane: "one-day", defaultDetails: "Fasting 12 hr", keywords: ["lipid", "cholesterol", "ldl", "hdl", "triglyceride", "tg"] },
  { name: "Thyroid function (TSH, FT4)", category: "lab", defaultLane: "one-day", defaultDetails: "Next morning 08:00", keywords: ["tft", "thyroid", "tsh", "ft4", "ft3", "thyroxine"] },
  { name: "Calcium / Magnesium / Phosphate", category: "lab", defaultLane: "one-day", defaultDetails: "Next morning 08:00", keywords: ["calcium", "ca", "magnesium", "mg", "phosphate", "po4", "electrolyte extended"] },
  { name: "Amylase / Lipase", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["amylase", "lipase", "pancreatitis", "pancreatic"] },
  { name: "C-reactive protein (CRP)", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["crp", "c-reactive", "inflammation", "inflammatory marker"] },
  { name: "ESR", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["esr", "sed rate", "erythrocyte sedimentation"] },
  { name: "Procalcitonin", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["procalcitonin", "pct", "sepsis marker"] },
  { name: "D-dimer", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["d-dimer", "ddimer", "vte", "pe", "dvt"] },
  { name: "NT-proBNP", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["bnp", "nt-probnp", "probnp", "heart failure", "chf"] },
  { name: "CK / CK-MB", category: "lab", defaultLane: "one-day", defaultDetails: "Now, serial", keywords: ["ck", "ck-mb", "ckmb", "creatine kinase", "cardiac enzyme"] },
  { name: "Venous blood gas (VBG)", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["vbg", "venous blood gas", "acid base"] },
  { name: "Blood group & cross-match", category: "lab", defaultLane: "one-day", defaultDetails: "Prepare for transfusion", keywords: ["blood group", "cross-match", "crossmatch", "group and match", "abo", "type and screen"] },
  { name: "Coagulogram (PT/PTT/INR)", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["coagulogram", "ptt", "aptt", "coagulation profile"] },
  { name: "HBsAg / Anti-HCV / Anti-HIV", category: "lab", defaultLane: "one-day", defaultDetails: "With consent", keywords: ["hbsag", "hepatitis", "anti-hcv", "hcv", "anti-hiv", "hiv", "viral serology"] },
  { name: "Dengue NS1 / IgM-IgG", category: "lab", defaultLane: "one-day", defaultDetails: "Now", keywords: ["dengue", "ns1", "dengue serology", "df", "dhf"] },
  { name: "Malaria blood smear", category: "lab", defaultLane: "one-day", defaultDetails: "Thick & thin film", keywords: ["malaria", "blood smear", "thick film", "thin film", "parasite"] },
  { name: "Sputum AFB", category: "lab", defaultLane: "one-day", defaultDetails: "×3 morning specimens", keywords: ["sputum afb", "afb", "tb", "tuberculosis", "acid fast"] },
  { name: "Sputum Gram stain & C/S", category: "lab", defaultLane: "one-day", defaultDetails: "Before antibiotics", keywords: ["sputum", "gram stain", "sputum culture", "c/s", "sensitivity"] },
  { name: "Urine culture", category: "lab", defaultLane: "one-day", defaultDetails: "Clean-catch, before antibiotics", keywords: ["urine culture", "uc", "uti", "urine c/s"] },

  // Extended procedures
  { name: "MRI", category: "procedure", defaultLane: "one-day", defaultDetails: "Per protocol; screen for implants", keywords: ["mri", "magnetic resonance", "mri brain", "mri spine"] },
  { name: "Ultrasound KUB", category: "procedure", defaultLane: "one-day", defaultDetails: "Full bladder", keywords: ["kub", "ultrasound kub", "renal ultrasound", "urinary"] },
  { name: "Doppler ultrasound", category: "procedure", defaultLane: "one-day", defaultDetails: "Rule out DVT", keywords: ["doppler", "venous doppler", "dvt scan", "duplex"] },
  { name: "EEG", category: "procedure", defaultLane: "one-day", defaultDetails: "Routine", keywords: ["eeg", "electroencephalogram", "seizure"] },
  { name: "Gastroscopy (EGD)", category: "procedure", defaultLane: "one-day", defaultDetails: "NPO 6 hr, consent", keywords: ["egd", "gastroscopy", "endoscopy", "ogd", "upper gi scope"] },
  { name: "Colonoscopy", category: "procedure", defaultLane: "one-day", defaultDetails: "Bowel prep, consent", keywords: ["colonoscopy", "colon scope", "lower gi scope"] },
  { name: "Lumbar puncture", category: "procedure", defaultLane: "one-day", defaultDetails: "Consent; check platelets/INR first", keywords: ["lumbar puncture", "lp", "spinal tap", "csf"] },
  { name: "Paracentesis", category: "procedure", defaultLane: "one-day", defaultDetails: "Consent; US-guided", keywords: ["paracentesis", "ascitic tap", "ascites"] },
  { name: "Thoracentesis", category: "procedure", defaultLane: "one-day", defaultDetails: "Consent; US-guided", keywords: ["thoracentesis", "pleural tap", "pleural effusion"] },
  { name: "Central venous catheter", category: "procedure", defaultLane: "one-day", defaultDetails: "Consent; confirm tip on CXR", keywords: ["cvc", "central line", "central venous", "ij line"] },
  { name: "NG tube insertion", category: "procedure", defaultLane: "one-day", defaultDetails: "Confirm placement", keywords: ["ng tube", "nasogastric", "ngt", "ryle"] },
  { name: "Spirometry / PFT", category: "procedure", defaultLane: "one-day", defaultDetails: "Pre/post bronchodilator", keywords: ["spirometry", "pft", "pulmonary function", "lung function"] },
  { name: "Incision & drainage", category: "procedure", defaultLane: "one-day", defaultDetails: "Under local anesthesia", keywords: ["i&d", "incision and drainage", "abscess drainage"] },
  { name: "Suture / wound closure", category: "procedure", defaultLane: "one-day", defaultDetails: "Under local anesthesia", keywords: ["suture", "wound closure", "laceration repair", "stitch"] },
];

/** Search the order catalog for autocomplete suggestions. */
export function searchOrderCatalog(query: string, limit = 8): CatalogItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored = orderCatalog
    .map((item) => {
      const haystacks = [item.name.toLowerCase(), ...item.keywords];
      let score = 0;
      for (const token of q.split(/\s+/)) {
        if (!token) continue;
        if (item.name.toLowerCase().startsWith(token)) score += 3;
        if (haystacks.some((h) => h.startsWith(token))) score += 2;
        if (haystacks.some((h) => h.includes(token))) score += 1;
      }
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
  return scored.map((entry) => entry.item);
}

export function catalogItemToOrder(item: CatalogItem, preferredLane?: OrderLane): ParsedOrder {
  return {
    id: makeId("cat"),
    category: item.category,
    label: item.name,
    details: item.defaultDetails,
    lane: preferredLane ?? item.defaultLane,
    stat: /stat|now/i.test(item.defaultDetails),
    source: "catalog",
    ...orderMeta(),
  };
}

// ---------------------------------------------------------------------------
// Smart free-text / dictation order parser
// ---------------------------------------------------------------------------

const ROUTE_MAP: Record<string, string> = {
  po: "PO", oral: "PO", iv: "IV", im: "IM", sc: "SC", sq: "SC", subcut: "SC",
  sl: "SL", pr: "PR", ng: "NG", inh: "INH", neb: "NEB", top: "Topical", id: "ID",
};

const FREQ_MAP: Record<string, string> = {
  od: "OD", daily: "OD", qd: "OD", bid: "BID", bd: "BID", tid: "TID", tds: "TID",
  qid: "QID", qds: "QID", hs: "HS", ac: "AC", pc: "PC", prn: "PRN",
  q4h: "q4h", q6h: "q6h", q8h: "q8h", q12h: "q12h", q24h: "q24h",
  stat: "STAT", once: "once", od_daily: "OD",
};

const CONTINUOUS_HINTS = /(continue|continu|cont\b|standing|maintenance|drip|infusion|\/\s?hr|\/\s?h\b|per hour|cc\s?\/?\s?hr|ml\s?\/?\s?hr|q\d+h|bid|tid|qid|\bod\b|\bhs\b|daily|scheduled|keep|titrate)/i;
const ONE_DAY_HINTS = /(stat|now|once|single|x\s?1\b|today|tomorrow|pre-?op|on call|before|draw|repeat|one[-\s]?time)/i;

function splitSegments(input: string): string[] {
  return input
    .split(/\s*(?:,|;|\n|\r|\bthen\b|\band also\b|\+)\s*/i)
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function detectCategory(text: string, catalog?: CatalogItem): OrderCategory {
  if (catalog) return catalog.category;
  const t = text.toLowerCase();
  if (/(nss|saline|dextrose|d5|ringer|rls|\blr\b|fluid|kcl|infus)/.test(t)) return "fluid";
  if (/(cbc|electrolyte|chem7|inr|\bpt\b|troponin|lactate|culture|urinalysis|\bua\b|abg|level|blood gas|occult|\blab\b|\bcr\b|\bbun\b)/.test(t)) return "lab";
  if (/(ecg|ekg|x-?ray|cxr|\bct\b|ultrasound|\bus\b|echo|catheter|foley|dressing|scan|endoscopy|\bmri\b)/.test(t)) return "procedure";
  if (/(\d+\s?(mg|mcg|g|units?|iu|ml|mmol|meq)\b|tab|cap|po|iv|im|\bsc\b|\bsl\b|prn|bid|tid|qid|\bod\b|\bhs\b|inject|drug|med)/.test(t)) return "medication";
  return "other";
}

function extractDose(text: string): string | null {
  const match = text.match(/(\d+(?:\.\d+)?)\s?(mg|mcg|g|units?|iu|ml|mmol|meq|%)\b/i);
  return match ? `${match[1]} ${match[2].toLowerCase()}` : null;
}

function extractRate(text: string): string | null {
  const match = text.match(/(\d+(?:\.\d+)?)\s?(cc|ml)\s?\/?\s?(hr|h|hour)\b/i);
  return match ? `${match[1]} mL/hr` : null;
}

function extractRoute(text: string): string | null {
  for (const token of text.toLowerCase().split(/[\s/]+/)) {
    if (ROUTE_MAP[token]) return ROUTE_MAP[token];
  }
  return null;
}

function extractFrequency(text: string): string | null {
  const t = text.toLowerCase();
  // "1x3" / "1×3" style → dose count × times per day
  const cross = t.match(/\b(\d)\s?[x×]\s?(\d)\b/);
  if (cross) {
    const perDay = Number(cross[2]);
    const map: Record<number, string> = { 1: "OD", 2: "BID", 3: "TID", 4: "QID" };
    return map[perDay] ?? `${perDay}×/day`;
  }
  for (const token of t.split(/[\s]+/)) {
    if (FREQ_MAP[token]) return FREQ_MAP[token];
  }
  return null;
}

function extractTiming(text: string): string | null {
  const t = text.toLowerCase();
  if (/tomorrow|พรุ่งนี้/.test(t)) return "Tomorrow 08:00";
  if (/tonight|คืนนี้/.test(t)) return "Tonight";
  if (/\bnow\b|stat|เดี๋ยวนี้/.test(t)) return "Now";
  if (/on call|pre-?op/.test(t)) return "On call / pre-op";
  return null;
}

function titleCase(text: string): string {
  const clean = text.replace(/\b(stat|now|once|continue|tomorrow|tonight)\b/gi, "").replace(/\s+/g, " ").trim();
  if (!clean) return text.trim();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function detectLane(text: string, category: OrderCategory, hasRate: boolean, preferred?: OrderLane): OrderLane {
  if (preferred) return preferred;
  if (/\bstat\b|\bnow\b|\bonce\b|x\s?1\b|single/i.test(text)) return "one-day";
  if (hasRate || CONTINUOUS_HINTS.test(text)) return "continuous";
  if (category === "lab" || category === "procedure") return "one-day";
  if (ONE_DAY_HINTS.test(text)) return "one-day";
  // Standing medications/fluids default to continuous when a recurring schedule
  // is implied; otherwise a one-off order.
  if (category === "medication" && /(bid|tid|qid|q\d+h|\bod\b|\bhs\b|daily)/i.test(text)) return "continuous";
  return "one-day";
}

function parseSegment(segment: string, preferred?: OrderLane): ParsedOrder | null {
  const normalized = segment.trim();
  if (!normalized) return null;

  const [catalog] = searchOrderCatalog(normalized, 1);
  const category = detectCategory(normalized, catalog);
  const dose = extractDose(normalized);
  const rate = extractRate(normalized);
  const route = extractRoute(normalized);
  const frequency = extractFrequency(normalized);
  const timing = extractTiming(normalized);
  const stat = /\bstat\b|\bnow\b/i.test(normalized);
  const lane = detectLane(normalized, category, Boolean(rate), preferred);

  // Build label: prefer catalog name; else the leading words + dose.
  let label: string;
  if (catalog) {
    label = dose && !new RegExp(dose.split(" ")[1], "i").test(catalog.name)
      ? `${catalog.name.replace(/\s\d.*$/, "")} ${dose}`
      : catalog.name;
  } else {
    const words = normalized.replace(/\d+(?:\.\d+)?\s?(mg|mcg|g|units?|iu|ml|mmol|meq|cc|%).*$/i, "").trim();
    label = titleCase(words.split(/\s+/).slice(0, 4).join(" ") || normalized);
    if (dose) label = `${label} ${dose}`.trim();
  }

  const detailParts = [rate, route, frequency, timing].filter(Boolean) as string[];
  const details =
    detailParts.length > 0
      ? detailParts.join(" · ")
      : catalog?.defaultDetails ?? "Needs confirmation";

  return {
    id: makeId(category),
    category,
    label: label || titleCase(normalized),
    details,
    lane,
    stat,
    source: "parsed",
    ...orderMeta(),
  };
}

export async function parseOrderText(input: string, preferredLane?: OrderLane): Promise<ParsedOrder[]> {
  await delay();
  const segments = splitSegments(input);
  const orders = segments
    .map((segment) => parseSegment(segment, preferredLane))
    .filter((order): order is ParsedOrder => order !== null);

  if (!orders.length && input.trim()) {
    orders.push({
      id: makeId("other"),
      category: "other",
      label: titleCase(input.trim()),
      details: "Needs confirmation",
      lane: preferredLane ?? "one-day",
      stat: /stat/i.test(input),
      source: "parsed",
      ...orderMeta(),
    });
  }

  // Flag a known allergy conflict for demo realism (Penicillin).
  return orders.map((order) =>
    /amoxicillin|penicillin|augmentin|ceftriaxone/i.test(order.label)
      ? { ...order, conflict: "Allergy check: Penicillin recorded — verify before signing" }
      : order,
  );
}

// ---------------------------------------------------------------------------
// SOAP dictation
// ---------------------------------------------------------------------------

export const ghostSuggestions: GhostSuggestion[] = [
  {
    field: "subjective",
    text: "Fatigue and exertional dyspnea; no active bleeding reported.",
    evidence: "Hb 7.2 g/dL · CBC 08:00",
    why: "Claim-critical symptom evidence for active Anemia (D64.9); CPG-Anemia §3.1.",
  },
  {
    field: "objective",
    text: "Pale conjunctivae. Hb 7.2 g/dL (08:00).",
    evidence: "CBC 08:00 · physical exam gap",
    why: "Objective evidence is missing for active Anemia (D64.9); CPG-Anemia §3.1.",
  },
];

export async function processDictation(field: SoapSection): Promise<GhostSuggestion> {
  await delay(650);
  const textByField: Record<SoapSection, string> = {
    subjective: "Patient reports progressive fatigue on exertion. No melena or hematemesis.",
    objective: "Pale conjunctivae noted. No active external bleeding. Hemodynamically stable.",
    assessment: "Symptomatic anemia; correlate symptoms with Hb trend and evaluate the underlying cause.",
    plan: "Review transfusion threshold, confirm consent, and repeat CBC after intervention.",
  };

  return {
    field,
    text: textByField[field],
    evidence: "Recorded dictation · Thai medical ASR simulation",
    why: "The transcript is held as a suggestion so the doctor can review and edit it before adding it to the legal note.",
  };
}

/**
 * One-press "Scribe": a single recording is structured into all four SOAP
 * sections at once, each returned as a reviewable suggestion routed to its
 * field. Mirrors the transcribe-then-structure flow of the real gateway.
 */
export async function processFullScribe(): Promise<GhostSuggestion[]> {
  await delay(900);
  const evidence = "Ward-round dictation · Thai/English ASR simulation · CBC 08:00";
  return [
    {
      field: "subjective",
      text: "Reports progressive fatigue on exertion over the last two days. No melena, hematemesis, or dizziness. Appetite fair.",
      evidence,
      why: "Structured from the Subjective portion of your dictation; review before it enters the note.",
      mode: "simulation",
    },
    {
      field: "objective",
      text: "Alert, not in distress. Pale conjunctivae. No active external bleeding. BP 118/72, HR 82, SpO₂ 97% RA. Hb 7.2 g/dL (08:00).",
      evidence,
      why: "Structured from the Objective portion of your dictation and current vitals/labs.",
      mode: "simulation",
    },
    {
      field: "assessment",
      text: "Symptomatic anemia (D64.9), Hb 7.2 g/dL; etiology under evaluation, GI blood loss to be excluded.",
      evidence,
      why: "Structured from the Assessment portion; diagnosis remains the clinician's decision.",
      mode: "simulation",
    },
    {
      field: "plan",
      text: "Repeat CBC in AM, send stool occult blood, review transfusion threshold and consent, continue current medications.",
      evidence,
      why: "Structured from the Plan portion of your dictation; each order still requires confirmation.",
      mode: "simulation",
    },
  ];
}

// ---------------------------------------------------------------------------
// Help Desk intent engine (richer, demo-ready, still documentation-focused)
// ---------------------------------------------------------------------------

type HelpReply = { text: string; citation: string };

const helpIntents: { match: RegExp; reply: HelpReply }[] = [
  {
    match: /(transfus|prc|packed red|\bhb\b|hemoglobin|anemia|anaemia)/i,
    reply: {
      text: "Hb is 7.2 g/dL (down from 8.1 yesterday) with documented fatigue. Institutional guidance suggests considering PRC transfusion under ~7 g/dL, or under 8 with symptoms/cardiac risk. Before ordering PRC, the note should confirm symptoms, active-bleeding status, the transfusion threshold, and informed consent. I can stage those fields and a draft PRC order for your review — the decision stays with you.",
      citation: "CBC 08:00 · CPG-Anemia §4.2",
    },
  },
  {
    match: /(inr|warfarin|bleed|coag|anticoag)/i,
    reply: {
      text: "Warfarin and Amoxicillin together can raise INR. If both are continued I'd document closer INR monitoring and reassess bleeding risk; a PT/INR draw tomorrow AM is reasonable. I can draft that lab order and add the interaction note to your Plan for review.",
      citation: "MAR review · interaction rules v4",
    },
  },
  {
    match: /(magnesium|\bmg\b|electrolyte|\bpr\b interval|prolong|arrhythm)/i,
    reply: {
      text: "Magnesium was borderline yesterday at 1.6 mg/dL (normal ~1.7–2.2). Hypomagnesemia can contribute to PR/QT changes, so I'd correct reversible electrolytes before attributing ECG changes elsewhere. Repeat electrolytes and consider replacement per protocol — please confirm.",
      citation: "Chem7 08:00",
    },
  },
  {
    match: /(consult|cardio|refer)/i,
    reply: {
      text: "A cardiology consult may be reasonable if the PR prolongation persists or symptoms recur. I'd first review the current ECG and correct reversible factors (magnesium was borderline at 1.6). If you proceed, I can draft the consult order and summarize the relevant findings for the referral.",
      citation: "ECG 07:42 · Chem7 08:00",
    },
  },
  {
    match: /(dose|renal|creatinine|\bcr\b|adjust|kidney)/i,
    reply: {
      text: "Creatinine is mildly elevated at 1.3 mg/dL, so renally-cleared drugs warrant a dose check (e.g. Metformin is currently held for renal review). Tell me the drug and I'll surface the usual inpatient dose and any adjustment note for your confirmation.",
      citation: "Chem7 08:00 · MAR",
    },
  },
  {
    match: /(sepsis|infection|fever|lactate|culture)/i,
    reply: {
      text: "If sepsis is a concern, the first-hour bundle documentation should cover cultures before antibiotics, a lactate with a 2-hour repeat, fluid resuscitation, and a perfusion reassessment. I can pre-fill the Assessment/Plan skeleton and draft the bundle orders for your review.",
      citation: "PCT Sepsis pathway",
    },
  },
  {
    match: /(gap|claim|document|coding|audit|complete)/i,
    reply: {
      text: "I found a claim-critical documentation gap for the active anemia record: the Subjective note doesn't yet state bleeding status, and Objective lacks a pallor finding with the timed Hb. Both are needed to support D64.9 on review. I can suggest wording for each — you remain the final author.",
      citation: "D64.9 evidence schema v2 · CPG-Anemia §3.1",
    },
  },
  {
    match: /(discharge|home|d\/c)/i,
    reply: {
      text: "For discharge readiness on this anemia admission I'd confirm a stable/last Hb trend, a documented etiology or follow-up plan, medication reconciliation, and outpatient labs. Say the word and I'll draft a discharge-summary skeleton for review.",
      citation: "Discharge criteria · CPG-Anemia",
    },
  },
];

export async function getHelpDeskReply(input: string): Promise<HelpReply> {
  await delay();
  const found = helpIntents.find((intent) => intent.match.test(input));
  if (found) return found.reply;
  return {
    text: "I can help with documentation completeness, evidence review, order drafting, and claim-readiness for this patient. Try asking about the anemia/transfusion threshold, the Warfarin–Amoxicillin interaction, the borderline magnesium, or which documentation gaps remain. I'll ground answers in the current chart and leave the clinical decision to you.",
    citation: "Nong SATI · documentation assistant",
  };
}

// ---------------------------------------------------------------------------
// Templates (built-in + custom, persisted in localStorage)
// ---------------------------------------------------------------------------

export const templates: TemplatePreview[] = [
  {
    id: "post-op",
    name: "Post-Op Routine",
    subtitle: "Routine post-operative review + recovery orders",
    soap: {
      subjective: "Pain controlled. No nausea or vomiting. Tolerating oral intake.",
      objective: "Afebrile. Wound clean and dry. Ambulating with assistance.",
      assessment: "Post-operative day 1; recovery progressing as expected.",
      plan: "Continue multimodal analgesia, early ambulation, and diet advancement.",
    },
    orders: [
      {
        id: "tpl-pct",
        category: "medication",
        label: "Paracetamol 500 mg",
        details: "PO q6h PRN pain",
        lane: "continuous",
        stat: false,
        source: "template",
      },
      {
        id: "tpl-cbc",
        category: "lab",
        label: "CBC",
        details: "Tomorrow 08:00",
        lane: "one-day",
        stat: false,
        source: "template",
      },
      {
        id: "tpl-amox",
        category: "medication",
        label: "Amoxicillin 500 mg",
        details: "PO tid × 5 days",
        lane: "continuous",
        stat: false,
        source: "template",
        conflict: "Allergy conflict: Penicillin — rash",
      },
    ],
  },
  {
    id: "sepsis",
    name: "PCT Sepsis Pathway",
    subtitle: "First-hour bundle with documentation skeleton",
    soap: {
      assessment: "Sepsis pathway activated; source and organ dysfunction under evaluation.",
      plan: "Obtain cultures, trend lactate, start fluids, and reassess perfusion.",
    },
    orders: [
      {
        id: "tpl-lac",
        category: "lab",
        label: "Serum lactate",
        details: "Now, repeat in 2 hr",
        lane: "one-day",
        stat: true,
        source: "template",
      },
      {
        id: "tpl-nss",
        category: "fluid",
        label: "NSS bolus",
        details: "500 mL IV, reassess",
        lane: "one-day",
        stat: true,
        source: "template",
      },
    ],
  },
  {
    id: "diabetes",
    name: "Diabetes Care",
    subtitle: "Glucose monitoring and inpatient medication review",
    soap: {
      assessment: "Type 2 diabetes; inpatient glycemic trend reviewed.",
      plan: "Continue capillary glucose monitoring and adjust treatment after review.",
    },
    orders: [
      {
        id: "tpl-dtx",
        category: "lab",
        label: "Capillary glucose",
        details: "AC & HS",
        lane: "continuous",
        stat: false,
        source: "template",
      },
    ],
  },
];

const CUSTOM_TEMPLATES_KEY = "sati.customTemplates.v1";

export function loadCustomTemplates(): TemplatePreview[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_TEMPLATES_KEY);
    return raw ? (JSON.parse(raw) as TemplatePreview[]) : [];
  } catch {
    return [];
  }
}

export function saveCustomTemplate(template: TemplatePreview): TemplatePreview[] {
  const existing = loadCustomTemplates().filter((item) => item.id !== template.id);
  const next = [template, ...existing];
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CUSTOM_TEMPLATES_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota errors in demo */
    }
  }
  return next;
}

export function deleteCustomTemplate(id: string): TemplatePreview[] {
  const next = loadCustomTemplates().filter((item) => item.id !== id);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CUSTOM_TEMPLATES_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }
  return next;
}

export async function loadTemplate(id: string): Promise<TemplatePreview> {
  await delay(450);
  const all = [...templates, ...loadCustomTemplates()];
  return all.find((template) => template.id === id) ?? templates[0];
}

export function createManualOrder(category: OrderCategory, lane: OrderLane = "one-day"): ParsedOrder {
  const labels: Record<OrderCategory, string> = {
    medication: "New medication",
    lab: "New laboratory test",
    procedure: "New procedure",
    fluid: "New IV fluid",
    other: "New clinical order",
  };
  return {
    id: makeId("manual"),
    category,
    label: labels[category],
    details: "Click to edit",
    lane,
    stat: false,
    source: "manual",
    ...orderMeta(),
  };
}
